import { Crosshair, Loader2, MapPin } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { AddressMapPicker } from "@/features/customer/components/address-map-picker";
import { useCustomerAddresses } from "@/features/customer/hooks/use-customer-addresses";
import { usePhoneVerificationStatus } from "@/features/customer/hooks/use-phone-verification";
import type { DeliveryCheckoutAddressFeedbackReason } from "@/features/delivery/checkout/delivery-checkout-model";
import { useDeliveryCheckout } from "@/features/delivery/checkout/use-delivery-checkout";
import { classifyDeliverySessionError, useDeliverySession } from "@/hooks/use-delivery-session";
import { authClient } from "@/lib/auth-client";
import { getAuthPath, isConsumerSession } from "@/lib/auth-roles";
import { redirectToExternalUrl } from "@/lib/browser-navigation";
import { type CartScope, useCart } from "@/lib/cart-store";
import { formatPrice } from "@/lib/format-price";

const SCOPE: CartScope = { mode: "delivery" };

/**
 * Translate a backend Address resolution reason code into structured i18n
 * copy. The frontend owns human-facing copy and keeps the backend reasons as
 * structured keys; this helper maps each reason to a stable translation key
 * so analytics and logs can rely on the code while users see localized text.
 */
function resolutionReasonCopy(
  t: (key: string, fallback: string) => string,
  reason: DeliveryCheckoutAddressFeedbackReason,
): string {
  switch (reason) {
    case "outside_country":
      return t(
        "delivery.checkout.addressReason.outside_country",
        "Este país todavía no está disponible para delivery.",
      );
    case "outside_coverage":
      return t(
        "delivery.checkout.addressReason.outside_coverage",
        "Esta dirección está fuera del área de delivery del restaurante.",
      );
    case "unsupported_location_precision":
      return t(
        "delivery.checkout.addressReason.unsupported_location_precision",
        "Solo pudimos ubicar la dirección a nivel de zona. Ajustá el pin al lugar exacto.",
      );
    case "pin_too_far":
      return t(
        "delivery.checkout.addressReason.pin_too_far",
        "El pin está lejos de la dirección. Volvé a ubicarlo cerca del domicilio.",
      );
    case "ambiguous":
      return t(
        "delivery.checkout.addressReason.ambiguous",
        "Encontramos varias direcciones parecidas. Elegí la correcta.",
      );
    case "provider_unavailable":
      return t(
        "delivery.checkout.addressReason.provider_unavailable",
        "No pudimos verificar la dirección ahora. Reintentá en unos minutos.",
      );
    case "provider_timeout":
      return t(
        "delivery.checkout.addressReason.provider_timeout",
        "La verificación de la dirección tardó demasiado. Reintentá.",
      );
    case "address_no_longer_deliverable":
      return t(
        "delivery.checkout.addressReason.address_no_longer_deliverable",
        "Esta dirección dejó de estar disponible para delivery. Volvé a verificarla.",
      );
    case "token_expired":
      return t(
        "delivery.checkout.addressReason.token_expired",
        "La verificación de la dirección expiró. La renovamos automáticamente.",
      );
    case "token_invalid":
      return t(
        "delivery.checkout.addressReason.token_invalid",
        "La verificación de la dirección ya no es válida. Reintentá.",
      );
    case "address_resolution_required":
      return t(
        "delivery.checkout.addressReason.address_resolution_required",
        "Tenemos que verificar la dirección antes de continuar.",
      );
    case "delivery_disabled":
      return t(
        "delivery.checkout.addressReason.delivery_disabled",
        "Este restaurante no ofrece envíos en este momento.",
      );
    case "delivery_unconfigured":
      return t(
        "delivery.checkout.addressReason.delivery_unconfigured",
        "El envío todavía no está configurado para este restaurante.",
      );
    case "invalid_coordinates":
      return t(
        "delivery.checkout.addressReason.invalid_coordinates",
        "Las coordenadas de la dirección no son válidas. Ubicá el pin en el mapa.",
      );
    case "address_not_found":
      return t(
        "delivery.checkout.addressReason.address_not_found",
        "No encontramos esta dirección guardada. Volvé a elegir una.",
      );
    case "rate_limited":
      return t(
        "delivery.checkout.addressReason.rate_limited",
        "Estás haciendo demasiadas verificaciones. Esperá unos segundos antes de reintentar.",
      );
    case "map_unavailable":
      return t(
        "delivery.checkout.addressReason.map_unavailable",
        "No pudimos cargar el mapa. Usá una dirección guardada o pedí permiso de ubicación para continuar.",
      );
    case "geolocation_denied":
      return t(
        "delivery.checkout.addressReason.geolocation_denied",
        "Bloqueaste el permiso de ubicación. Podés escribir la dirección manualmente.",
      );
    case "geolocation_unavailable":
      return t(
        "delivery.checkout.addressReason.geolocation_unavailable",
        "No pudimos leer tu ubicación ahora. Escribí la dirección manualmente.",
      );
    case "geolocation_timeout":
      return t(
        "delivery.checkout.addressReason.geolocation_timeout",
        "La lectura de tu ubicación tardó demasiado. Reintentá o escribí la dirección.",
      );
    case "geolocation_unsupported":
      return t(
        "delivery.checkout.addressReason.geolocation_unsupported",
        "Tu navegador no permite leer la ubicación. Escribí la dirección manualmente.",
      );
    case "needs_review":
    case "verified":
      return t(
        "delivery.checkout.addressReason.unspecified",
        "Tenemos que verificar esta dirección antes de continuar.",
      );
    default:
      reason satisfies never;
      return "";
  }
}

export default function DeliveryCheckoutPage() {
  const { t } = useTranslation();
  const { orgSlug } = useParams();
  const navigate = useNavigate();

  const authSession = authClient.useSession();
  const isAuthenticated = Boolean(authSession.data);
  const isConsumer = isConsumerSession(authSession.data);
  const sessionQuery = useDeliverySession(orgSlug);
  const addressesQuery = useCustomerAddresses({
    enabled: isAuthenticated && isConsumer,
  });
  const phoneStatus = usePhoneVerificationStatus({
    enabled: isAuthenticated && isConsumer,
  });
  const cart = useCart(orgSlug, SCOPE);

  const payNowAvailable = Boolean(sessionQuery.data?.payNowAvailable);
  const cashOnDeliveryAvailable = Boolean(sessionQuery.data?.cashOnDeliveryAvailable);
  const emailVerified = Boolean(authSession.data?.user.emailVerified);
  const phoneVerified = Boolean(phoneStatus.data?.verified);
  const participant = !isAuthenticated
    ? ({ kind: "guest" } as const)
    : isConsumer
      ? ({ kind: "consumer", emailVerified, phoneVerified } as const)
      : ({ kind: "restaurant_account" } as const);

  const accountDataPending =
    authSession.isPending ||
    (isAuthenticated && isConsumer && (addressesQuery.isPending || phoneStatus.isPending));
  const checkoutReady =
    Boolean(orgSlug) &&
    !sessionQuery.isPending &&
    !accountDataPending &&
    !sessionQuery.isError &&
    Boolean(sessionQuery.data) &&
    !(isAuthenticated && !isConsumer);
  const addresses = isAuthenticated ? (addressesQuery.data ?? []) : [];
  const checkout = useDeliveryCheckout({
    addresses,
    cashOnDeliveryAvailable,
    cart: { itemCount: cart.itemCount, lines: cart.state.lines },
    checkoutReady,
    inlineAddressLabel: t("delivery.checkout.inlineAddress", "Entrega"),
    orgSlug: orgSlug ?? "",
    participant,
    payNowAvailable,
    pricing: {
      deliveryFee: sessionQuery.data?.deliveryFeeFlat ?? 0,
      deliveryMinimumOrder: sessionQuery.data?.deliveryMinimumOrder ?? 0,
      subtotal: cart.subtotal,
    },
  });
  const activeAddress = checkout.address.active;
  const inlineAddress = activeAddress.kind === "inline" ? activeAddress : null;
  const inlineAddressCandidates =
    inlineAddress?.resolution.status === "blocked" ? inlineAddress.resolution.candidates : [];
  const browserLocationFeedback = inlineAddress?.map.browserLocation.feedbackReason
    ? resolutionReasonCopy(t, inlineAddress.map.browserLocation.feedbackReason)
    : null;

  useEffect(() => {
    if (!checkout.feedback.error) {
      return;
    }
    toast.error((checkout.feedback.error as Error).message);
    checkout.feedback.dismissError();
  }, [checkout.feedback.error, checkout.feedback.dismissError]);

  if (!orgSlug) {
    return null;
  }

  const checkoutPath = `/m/${orgSlug}/delivery/checkout`;

  if (sessionQuery.isPending || accountDataPending) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (sessionQuery.isError || !sessionQuery.data) {
    const availability = classifyDeliverySessionError(sessionQuery.error ?? null);
    if (availability === "disabled") {
      return (
        <div className="mx-auto max-w-xl p-6 text-center">
          <p className="text-muted-foreground">
            {t(
              "delivery.checkout.deliveryDisabled",
              "Este restaurante no ofrece envíos en este momento.",
            )}
          </p>
        </div>
      );
    }
    if (availability === "unconfigured") {
      return (
        <div className="mx-auto max-w-xl p-6 text-center">
          <p className="text-muted-foreground">
            {t(
              "delivery.checkout.deliveryUnconfigured",
              "El envío está habilitado pero todavía no está configurado para este restaurante. Pedí a un administrador que complete el origen y la cobertura.",
            )}
          </p>
        </div>
      );
    }
    return (
      <div className="mx-auto max-w-xl p-6 text-center">
        <p className="text-muted-foreground">{(sessionQuery.error as Error)?.message ?? "Error"}</p>
      </div>
    );
  }

  const session = sessionQuery.data;
  const handleSwitchToConsumer = async (action: "sign-in" | "sign-up") => {
    await authClient.signOut();
    navigate(getAuthPath("consumer", action, checkoutPath));
  };

  if (checkout.access.kind === "restaurant_account") {
    return (
      <div className="mx-auto max-w-xl p-6">
        <Card>
          <CardHeader>
            <CardTitle>
              {t("delivery.checkout.consumerAccountTitle", "Cuenta de consumidor")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground text-sm">
              {t(
                "delivery.checkout.consumerAccountRequired",
                "Para pedir delivery necesitás entrar con una cuenta de consumidor.",
              )}
            </p>
            <p className="text-muted-foreground text-sm">
              {t(
                "delivery.checkout.consumerAccountCartPreserved",
                "Tu carrito queda guardado mientras cambiás de cuenta.",
              )}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" onClick={() => handleSwitchToConsumer("sign-in")}>
                {t("delivery.checkout.consumerAccountSignIn", "Entrar como consumidor")}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleSwitchToConsumer("sign-up")}
              >
                {t("delivery.checkout.consumerAccountCreateAccount", "Crear cuenta de consumidor")}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const phoneVerificationPath = `/account/phone-verify?next=/m/${orgSlug}/delivery/checkout`;

  const handleSubmit = async () => {
    try {
      const result = await checkout.submission.submit();
      if (result.status === "blocked") {
        if (result.reason === "payment_unavailable") {
          toast.error(t("delivery.checkout.selectPayment", "Elegí una forma de pago disponible"));
        } else if (result.reason === "address_resolution_required") {
          toast.error(t("delivery.checkout.selectAddress", "Elegí un domicilio y ubicá el pin"));
        } else if (typeof result.reason === "object") {
          toast.error(resolutionReasonCopy(t, result.reason.code) + ` (${result.reason.code})`);
        } else if (result.reason === "map_unavailable") {
          toast.error(resolutionReasonCopy(t, result.reason) + ` (${result.reason})`);
        } else if (result.reason === "empty_cart") {
          toast.error(t("delivery.checkout.emptyCart", "Tu carrito está vacío."));
        } else if (result.reason === "minimum_order_not_met") {
          toast.error(
            t("delivery.checkout.minimumOrderRequired", "No alcanzaste el pedido mínimo."),
          );
        } else if (result.reason === "restaurant_account") {
          toast.error(
            t(
              "delivery.checkout.consumerAccountRequired",
              "Para pedir delivery necesitás entrar con una cuenta de consumidor.",
            ),
          );
        } else {
          toast.error(t("delivery.checkout.submissionPending", "El pedido ya se está enviando."));
        }
        return;
      }
      cart.clear();
      if (result.value.checkoutUrl) {
        redirectToExternalUrl(result.value.checkoutUrl);
        return;
      }
      navigate(`/m/${orgSlug}/delivery/order/${result.value.order.id}`);
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-4 p-4">
      <h1 className="text-2xl font-semibold">
        {t("delivery.checkout.title", "Confirmá tu pedido")}
      </h1>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("delivery.checkout.deliveringTo", "Entregar en")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {checkout.address.choices.length > 0 ? (
            <RadioGroup
              value={activeAddress.kind === "saved" ? activeAddress.customerAddressId : "inline"}
              onValueChange={(value) =>
                checkout.actions.selectAddress(
                  value === "inline"
                    ? { kind: "inline" }
                    : { kind: "saved", customerAddressId: value },
                )
              }
              className="space-y-2"
            >
              {checkout.address.choices.map((address) => (
                <label
                  key={address.customerAddressId}
                  className="flex cursor-pointer items-start gap-3 rounded-md border p-3"
                >
                  <RadioGroupItem value={address.customerAddressId} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{address.label}</span>
                    </div>
                    <p className="text-muted-foreground text-sm">
                      {address.line1}
                      {address.line2 ? `, ${address.line2}` : ""}
                      {address.city ? ` — ${address.city}` : ""}
                    </p>
                  </div>
                </label>
              ))}
              <label className="flex cursor-pointer items-start gap-3 rounded-md border p-3">
                <RadioGroupItem value="inline" />
                <div className="flex-1">
                  <div className="font-medium">
                    {t("delivery.checkout.useAnotherAddress", "Usar otro domicilio")}
                  </div>
                  <p className="text-muted-foreground text-sm">
                    {t(
                      "delivery.checkout.useAnotherAddressDescription",
                      "Ingresá una dirección para este pedido sin guardarla en tu cuenta.",
                    )}
                  </p>
                </div>
              </label>
            </RadioGroup>
          ) : null}

          {activeAddress.kind === "saved" && activeAddress.resolution.status === "blocked" ? (
            <div className="space-y-1" data-testid="saved-address-resolution-reason">
              <p className="text-xs text-destructive">
                {resolutionReasonCopy(t, activeAddress.resolution.reason)}
              </p>
              <p className="text-muted-foreground text-xs">
                {t(
                  "delivery.checkout.addressReasonCode",
                  `Motivo: ${activeAddress.resolution.reason}`,
                )}
              </p>
            </div>
          ) : null}

          {inlineAddress ? (
            <div className="space-y-3">
              {inlineAddress.map.unavailable ? (
                <div
                  className="space-y-1 rounded-md border border-destructive/40 bg-destructive/5 p-3"
                  data-testid="map-unavailable-banner"
                >
                  <p className="text-xs font-medium text-destructive">
                    {resolutionReasonCopy(t, "map_unavailable")}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {t(
                      "delivery.checkout.mapUnavailableHint",
                      "Si tenés permiso, podemos usar tu ubicación actual como punto de partida.",
                    )}
                  </p>
                </div>
              ) : null}
              <AddressMapPicker
                lat={inlineAddress.value.lat}
                lng={inlineAddress.value.lng}
                onChange={checkout.actions.moveMapPin}
                onLoadError={checkout.actions.reportMapUnavailable}
                onLocateError={checkout.actions.reportMapLocationFailure}
              />
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={checkout.actions.useBrowserLocation}
                  disabled={inlineAddress.map.browserLocation.pending}
                  data-testid="use-my-location"
                >
                  {inlineAddress.map.browserLocation.pending ? (
                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                  ) : (
                    <Crosshair className="mr-1 h-3 w-3" />
                  )}
                  {t("delivery.checkout.useMyLocation", "Usar mi ubicación")}
                </Button>
                {browserLocationFeedback ? (
                  <p className="text-xs text-muted-foreground" data-testid="geolocation-feedback">
                    {browserLocationFeedback}
                  </p>
                ) : null}
              </div>
              {inlineAddress.map.isGeocoding ? (
                <p className="text-xs text-muted-foreground">
                  {t("delivery.checkout.geocoding", "Buscando dirección...")}
                </p>
              ) : null}
              <div className="space-y-1">
                <Label htmlFor="delivery-address-label">
                  {t("delivery.checkout.addressLabel", "Etiqueta (opcional)")}
                </Label>
                <Input
                  id="delivery-address-label"
                  maxLength={80}
                  onChange={(event) =>
                    checkout.actions.updateInlineAddress("label", event.target.value)
                  }
                  placeholder={t("delivery.checkout.addressLabelPlaceholder", "Casa")}
                  value={inlineAddress.value.label}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="delivery-address-line1">
                  {t("delivery.checkout.addressLine1", "Dirección")}
                </Label>
                <Input
                  id="delivery-address-line1"
                  maxLength={200}
                  onChange={(event) =>
                    checkout.actions.updateInlineAddress("line1", event.target.value)
                  }
                  placeholder={t(
                    "delivery.checkout.addressLine1Placeholder",
                    "Calle, número, apartamento",
                  )}
                  required
                  value={inlineAddress.value.line1}
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label htmlFor="delivery-address-line2">
                    {t("delivery.checkout.addressLine2", "Referencia (opcional)")}
                  </Label>
                  <Input
                    id="delivery-address-line2"
                    maxLength={200}
                    onChange={(event) =>
                      checkout.actions.updateInlineAddress("line2", event.target.value)
                    }
                    placeholder={t("delivery.checkout.addressLine2Placeholder", "Esquina, piso")}
                    value={inlineAddress.value.line2}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="delivery-address-city">
                    {t("delivery.checkout.city", "Ciudad (opcional)")}
                  </Label>
                  <Input
                    id="delivery-address-city"
                    maxLength={120}
                    onChange={(event) =>
                      checkout.actions.updateInlineAddress("city", event.target.value)
                    }
                    value={inlineAddress.value.city}
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="delivery-address-notes">
                  {t("delivery.checkout.addressNotes", "Indicaciones de entrega (opcional)")}
                </Label>
                <Textarea
                  id="delivery-address-notes"
                  maxLength={500}
                  onChange={(event) =>
                    checkout.actions.updateInlineAddress("notes", event.target.value)
                  }
                  rows={2}
                  value={inlineAddress.value.notes}
                />
              </div>
              {inlineAddressCandidates.length > 0 ? (
                <div
                  className="space-y-2 rounded-md border border-border bg-muted/30 p-3"
                  data-testid="address-candidates"
                >
                  <p className="text-xs font-medium">
                    {t("delivery.checkout.candidatesTitle", "Elegí la dirección correcta")}
                  </p>
                  <RadioGroup
                    value={
                      inlineAddress.value.line1 &&
                      inlineAddressCandidates.some(
                        (candidate) => candidate.line1 === inlineAddress.value.line1,
                      )
                        ? inlineAddress.value.line1
                        : ""
                    }
                    onValueChange={(line1) => {
                      const candidate = inlineAddressCandidates.find(
                        (item) => item.line1 === line1,
                      );
                      if (!candidate) {
                        return;
                      }
                      checkout.actions.applyAddressCandidate(candidate);
                    }}
                    className="space-y-2"
                  >
                    {inlineAddressCandidates.map((candidate) => (
                      <label
                        key={`${candidate.line1}-${candidate.lat}-${candidate.lng}`}
                        className="flex cursor-pointer items-start gap-3 rounded-md border bg-background p-2"
                      >
                        <RadioGroupItem value={candidate.line1} />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">{candidate.line1}</span>
                          </div>
                          <p className="text-muted-foreground text-xs">
                            {candidate.lat.toFixed(4)}, {candidate.lng.toFixed(4)}
                          </p>
                        </div>
                      </label>
                    ))}
                  </RadioGroup>
                </div>
              ) : null}
              {inlineAddress.resolution.status === "blocked" ? (
                <div className="space-y-1" data-testid="address-resolution-reason">
                  <p className="text-xs text-destructive">
                    {resolutionReasonCopy(t, inlineAddress.resolution.reason)}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {t(
                      "delivery.checkout.addressReasonCode",
                      `Motivo: ${inlineAddress.resolution.reason}`,
                    )}
                  </p>
                </div>
              ) : null}
              {inlineAddress.save.available ? (
                <div
                  className="space-y-2 rounded-md border border-border bg-muted/30 p-3"
                  data-testid="save-as-customer-address"
                >
                  <label className="flex cursor-pointer items-center gap-2 text-sm">
                    <Checkbox
                      checked={inlineAddress.save.enabled}
                      disabled={!inlineAddress.save.inputEnabled}
                      onCheckedChange={(value) =>
                        checkout.actions.setSaveInlineAddress(value === true)
                      }
                    />
                    <span>
                      {t(
                        "delivery.checkout.saveAsCustomerAddress",
                        "Guardar este domicilio para próximos pedidos",
                      )}
                    </span>
                  </label>
                  {inlineAddress.save.enabled ? (
                    <div className="space-y-1">
                      <Label htmlFor="delivery-save-label">
                        {t(
                          "delivery.checkout.saveAsCustomerAddressLabel",
                          "Etiqueta (ej. Casa, Oficina)",
                        )}
                      </Label>
                      <Input
                        id="delivery-save-label"
                        maxLength={60}
                        onChange={(event) =>
                          checkout.actions.setInlineAddressSaveLabel(event.target.value)
                        }
                        placeholder={t(
                          "delivery.checkout.saveAsCustomerAddressLabelPlaceholder",
                          "Casa",
                        )}
                        value={inlineAddress.save.label}
                      />
                      <p className="text-muted-foreground text-xs">
                        {t(
                          "delivery.checkout.saveAsCustomerAddressHelp",
                          "Solo guardamos direcciones verificadas. El domicilio queda asociado a tu cuenta y se omite para los repartidores.",
                        )}
                      </p>
                    </div>
                  ) : null}
                </div>
              ) : null}
              {inlineAddress.resolution.status === "pending" ? (
                <p className="text-xs text-muted-foreground">
                  {t("delivery.checkout.resolving", "Verificando dirección...")}
                </p>
              ) : null}
            </div>
          ) : null}

          <div className="grid gap-3 border-border border-t pt-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="delivery-contact-name">
                {t("delivery.checkout.contactName", "Nombre de contacto (opcional)")}
              </Label>
              <Input
                id="delivery-contact-name"
                maxLength={120}
                onChange={(event) => checkout.actions.setContactName(event.target.value)}
                value={checkout.address.contact.name}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="delivery-contact-phone">
                {t("delivery.checkout.contactPhone", "Teléfono de contacto (opcional)")}
              </Label>
              <Input
                id="delivery-contact-phone"
                maxLength={40}
                onChange={(event) => checkout.actions.setContactPhone(event.target.value)}
                placeholder="+59899..."
                type="tel"
                value={checkout.address.contact.phone}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("delivery.checkout.payment", "Forma de pago")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={checkout.payment.selected}
            onValueChange={(value) =>
              checkout.actions.selectPayment(value === "pay_later" ? "pay_later" : "pay_now")
            }
            className="space-y-2"
          >
            <label
              className={`flex flex-col gap-1 rounded-md border p-3 ${
                checkout.payment.payNow.available
                  ? "cursor-pointer"
                  : "cursor-not-allowed opacity-60"
              }`}
            >
              <div className="flex items-center gap-3">
                <RadioGroupItem value="pay_now" disabled={!checkout.payment.payNow.available} />
                <span>{t("delivery.checkout.payNow", "Pagar ahora con MercadoPago")}</span>
              </div>
              {!checkout.payment.payNow.available ? (
                <p className="ml-7 text-muted-foreground text-xs">
                  {t(
                    "delivery.checkout.payNowUnavailable",
                    "Este restaurante no acepta Mercado Pago para delivery.",
                  )}
                </p>
              ) : null}
            </label>
            <label
              className={`flex flex-col gap-1 rounded-md border p-3 ${
                checkout.payment.payLater.available
                  ? "cursor-pointer"
                  : "cursor-not-allowed opacity-60"
              }`}
            >
              <div className="flex items-center gap-3">
                <RadioGroupItem value="pay_later" disabled={!checkout.payment.payLater.available} />
                <span>{t("delivery.checkout.payLater", "Pagar al recibir (efectivo)")}</span>
              </div>
              {!checkout.payment.payLater.available ? (
                <div className="ml-7 text-xs text-muted-foreground">
                  {checkout.payment.payLater.unavailableReason === "not_offered" ? (
                    t(
                      "delivery.checkout.codNotOffered",
                      "Este restaurante no ofrece pago en efectivo",
                    )
                  ) : checkout.payment.payLater.unavailableReason === "guest_requires_sign_in" ? (
                    <>
                      {t(
                        "delivery.checkout.signInForPayLater",
                        "Para pagar al recibir necesitás iniciar sesión.",
                      )}{" "}
                      <Link
                        to={getAuthPath("consumer", "sign-in", checkoutPath)}
                        className="text-primary underline-offset-4 hover:underline"
                      >
                        {t("auth.consumer.signIn.title", "Entrar como consumidor")}
                      </Link>
                    </>
                  ) : checkout.payment.payLater.unavailableReason ===
                    "email_verification_required" ? (
                    t(
                      "delivery.checkout.emailVerificationRequired",
                      "Verificá tu email para habilitarlo.",
                    )
                  ) : checkout.payment.payLater.unavailableReason ===
                    "phone_verification_required" ? (
                    <>
                      {t(
                        "delivery.checkout.codDisabledDescription",
                        "Verificá tu teléfono para habilitarlo",
                      )}{" "}
                      —{" "}
                      <Link
                        to={phoneVerificationPath}
                        className="text-primary underline-offset-4 hover:underline"
                      >
                        {t("delivery.checkout.verifyPhone", "Verificar ahora")}
                      </Link>
                    </>
                  ) : (
                    t(
                      "delivery.checkout.consumerAccountRequired",
                      "Para pedir delivery necesitás entrar con una cuenta de consumidor.",
                    )
                  )}
                </div>
              ) : null}
            </label>
          </RadioGroup>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("delivery.checkout.notes", "Notas (opcional)")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={checkout.notes}
            onChange={(event) => checkout.actions.setNotes(event.target.value)}
            maxLength={500}
            rows={3}
            placeholder={t(
              "delivery.checkout.notesPlaceholder",
              "Algo especial sobre el pedido o la entrega",
            )}
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-1 pt-4">
          <div className="flex items-center justify-between text-sm">
            <span>{t("delivery.cart.subtotal", "Subtotal")}</span>
            <span className="tabular-nums">
              {formatPrice(checkout.orderSummary.subtotal, session.currency)}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span>{t("delivery.checkout.deliveryFee", "Costo de envío")}</span>
            <span className="tabular-nums">
              {formatPrice(checkout.orderSummary.deliveryFee, session.currency)}
            </span>
          </div>
          <div className="flex items-center justify-between font-semibold">
            <span>{t("delivery.cart.total", "Total")}</span>
            <span className="tabular-nums">
              {formatPrice(checkout.orderSummary.total, session.currency)}
            </span>
          </div>
          {!checkout.orderSummary.minimumOrder.met &&
          checkout.orderSummary.minimumOrder.amount > 0 ? (
            <p className="pt-2 text-xs text-destructive">
              {t("delivery.checkout.minOrderError", {
                amount: formatPrice(checkout.orderSummary.minimumOrder.amount, session.currency),
              })}
            </p>
          ) : null}
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate(`/m/${orgSlug}?fulfillment=delivery`)}
        >
          {t("common.back", "Volver")}
        </Button>
        <Button
          type="button"
          size="lg"
          onClick={handleSubmit}
          disabled={!checkout.submission.enabled || checkout.submission.pending}
        >
          {checkout.submission.pending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : checkout.payment.selected === "pay_now" ? (
            t("delivery.checkout.submitPayNow", "Pagar")
          ) : (
            t("delivery.checkout.submitPayLater", "Confirmar pedido")
          )}
        </Button>
      </div>
    </div>
  );
}
